/**
 * Synthesized sounds (Web Audio, no file): white noise through swept filters, and oscillators, shaped by short
 * envelopes. Display side only: nothing goes back into the game. The audio starts at the first click or key (the
 * browsers' autoplay rule). Two buses under the master: the effects and the music (music.ts); their volumes and the
 * mute are options, kept in the browser. A wind ambience follows the match's wind.
 */
import { WEAPON_BAZOOKA, WEAPON_DOLL, WEAPON_FLAMER, WEAPON_GEYSER, WEAPON_GRENADE, WEAPON_LASER, WEAPON_PISTOL, WEAPON_WAVE, type GameEvent } from '@invaders/game';
import type { Camera } from './camera';
import { loadOptions, saveOptions } from './options';

/** At most one sound of a kind in this many seconds (a trail of powder sets off dozens of blasts). */
const REPEAT_S = 0.06;

class Sound {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private effects: GainNode | null = null;
  private musicBus: GainNode | null = null;
  private wind: GainNode | null = null;
  private windLevel = 0;
  private noise: AudioBuffer | null = null;
  private unlocked: (() => void)[] = [];
  private last = new Map<string, number>();
  private cam: Camera | null = null;
  /** Sounds played so far (scripted browser tests: the audio cannot be heard there). */
  private played = 0;

  constructor() {
    const unlock = () => this.unlock();
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
  }

  get muted(): boolean { return loadOptions().muted; }

  /** A volume of the options: all, the music, or the effects (0 to 1). */
  setLevel(which: 'volume' | 'music' | 'effects', v: number): void {
    saveOptions({ [which]: Math.min(1, Math.max(0, v)) });
    this.applyVolume();
  }
  setMuted(m: boolean): void { saveOptions({ muted: m }); this.applyVolume(); }

  /** The audio context and the music's bus, once the audio is unlocked (null before). */
  audio(): { ctx: AudioContext; bus: GainNode } | null {
    return this.ctx && this.musicBus ? { ctx: this.ctx, bus: this.musicBus } : null;
  }

  /** Called once the audio starts (at once if it already has). */
  onUnlock(f: () => void): void {
    if (this.ctx) f(); else this.unlocked.push(f);
  }

  /** For scripted tests: the audio context's state and the number of sounds played. */
  debug(): { state: string; played: number } {
    return { state: this.ctx?.state ?? 'none', played: this.played };
  }

  /** The camera, to place the sounds (pan by position, quieter far from the view). */
  follow(cam: Camera): void { this.cam = cam; }

  private unlock(): void {
    if (this.ctx) { if (this.ctx.state === 'suspended') void this.ctx.resume(); return; }
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return;
    this.ctx = new AC();
    this.master = this.ctx.createGain();
    this.master.connect(this.ctx.destination);
    this.effects = this.ctx.createGain();
    this.effects.connect(this.master);
    this.musicBus = this.ctx.createGain();
    this.musicBus.connect(this.master);
    this.applyVolume();
    const n = this.ctx.sampleRate;
    this.noise = this.ctx.createBuffer(1, n * 2, n);
    const d = this.noise.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    // The wind: a loop of noise through a low-pass filter whose loudness follows the match's wind.
    const src = this.ctx.createBufferSource();
    src.buffer = this.noise;
    src.loop = true;
    const f = this.ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = 420;
    this.wind = this.ctx.createGain();
    this.wind.gain.value = 0;
    src.connect(f).connect(this.wind).connect(this.effects);
    src.start();
    for (const cb of this.unlocked.splice(0)) cb();
  }

  private applyVolume(): void {
    const o = loadOptions();
    if (this.master) this.master.gain.value = o.muted ? 0 : o.volume * o.volume;
    if (this.effects) this.effects.gain.value = o.effects * o.effects * 0.8;
    if (this.musicBus) this.musicBus.gain.value = o.music * o.music * 0.6;
  }

  /** The wind ambience: 0 (calm) to 1 (wind 10). */
  setWind(level: number): void {
    if (!this.ctx || !this.wind || level === this.windLevel) return;
    this.windLevel = level;
    this.wind.gain.setTargetAtTime(Math.min(1, Math.max(0, level)) * 0.16, this.ctx.currentTime, 0.8);
  }

  /** Pan (-1 to 1) and loudness (0 to 1) of a sound at cell (x, y). */
  private where(x?: number, y?: number): [number, number] {
    const cam = this.cam;
    if (!cam || x === undefined || y === undefined) return [0, 1];
    const cx = cam.x + cam.spanW / 2;
    const cy = cam.y + cam.spanH / 2;
    const pan = Math.max(-1, Math.min(1, (x - cx) / (cam.spanW / 2))) * 0.7;
    const out = Math.max(0, Math.hypot(x - cx, y - cy) - cam.spanW / 2);
    return [pan, 1 / (1 + out / 160)];
  }

  /** False if a sound of this kind just played (and then it does not play). */
  private allow(kind: string): boolean {
    if (!this.ctx || this.muted) return false;
    const t = this.ctx.currentTime;
    if ((this.last.get(kind) ?? -1) > t - REPEAT_S) return false;
    this.last.set(kind, t);
    this.played++;
    return true;
  }

  private out(pan: number, gain: number): AudioNode {
    const ctx = this.ctx!;
    const g = ctx.createGain();
    g.gain.value = gain;
    if (ctx.createStereoPanner) {
      const p = ctx.createStereoPanner();
      p.pan.value = pan;
      g.connect(p).connect(this.effects!);
    } else g.connect(this.effects!);
    return g;
  }

  /** An oscillator sweeping from f0 to f1 Hz, with a quick attack and an exponential decay. */
  private tone(type: OscillatorType, f0: number, f1: number, dur: number, gain: number, pan = 0, delay = 0): void {
    const ctx = this.ctx!;
    const t = ctx.currentTime + delay;
    const o = ctx.createOscillator();
    const env = ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(f0, t);
    o.frequency.exponentialRampToValueAtTime(Math.max(1, f1), t + dur);
    env.gain.setValueAtTime(0.0001, t);
    env.gain.exponentialRampToValueAtTime(1, t + 0.008);
    env.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(env).connect(this.out(pan, gain));
    o.start(t);
    o.stop(t + dur + 0.02);
  }

  /** White noise through a filter swept from f0 to f1 Hz, with an exponential decay. */
  private hiss(filter: BiquadFilterType, f0: number, f1: number, dur: number, gain: number, pan = 0, delay = 0, q = 1): void {
    const ctx = this.ctx!;
    const t = ctx.currentTime + delay;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    src.loop = true;
    const f = ctx.createBiquadFilter();
    f.type = filter;
    f.Q.value = q;
    f.frequency.setValueAtTime(f0, t);
    f.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t + dur);
    const env = ctx.createGain();
    env.gain.setValueAtTime(0.0001, t);
    env.gain.exponentialRampToValueAtTime(1, t + 0.01);
    env.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(f).connect(env).connect(this.out(pan, gain));
    src.start(t, Math.random() * 0.5);
    src.stop(t + dur + 0.02);
  }

  // --- the game's sounds ------------------------------------------------------------------------------

  explosion(radius: number, x?: number, y?: number): void {
    if (!this.allow('explosion')) return;
    const [pan, g] = this.where(x, y);
    const size = Math.min(1.5, 0.4 + radius / 40);
    this.hiss('lowpass', 2400, 120, 0.5 + size * 0.6, 0.9 * g * size, pan);
    this.tone('sine', 110, 32, 0.4 + size * 0.4, 0.8 * g * size, pan);
  }

  thud(x?: number, y?: number): void {
    if (!this.allow('thud')) return;
    const [pan, g] = this.where(x, y);
    this.hiss('lowpass', 600, 100, 0.18, 0.5 * g, pan);
  }

  /** Where the bullet hits (its shot is heard at the launch). */
  shot(x?: number, y?: number): void {
    if (!this.allow('shot')) return;
    const [pan, g] = this.where(x, y);
    this.hiss('highpass', 2500, 900, 0.06, 0.35 * g, pan);
  }

  launch(weapon: number, x?: number, y?: number): void {
    const [pan, g] = this.where(x, y);
    if (!this.allow(`launch${weapon}`)) return;
    if (weapon === WEAPON_PISTOL) { this.hiss('highpass', 3000, 1200, 0.09, 0.6 * g, pan); this.tone('square', 900, 200, 0.07, 0.12 * g, pan); }
    else if (weapon === WEAPON_LASER) this.tone('square', 1600, 180, 0.35, 0.22 * g, pan);
    else if (weapon === WEAPON_BAZOOKA) { this.hiss('bandpass', 400, 2000, 0.35, 0.5 * g, pan, 0, 2); this.tone('sawtooth', 90, 60, 0.15, 0.15 * g, pan); }
    else if (weapon === WEAPON_FLAMER) this.hiss('bandpass', 700, 300, 1.5, 0.45 * g, pan, 0, 0.7);
    else if (weapon === WEAPON_WAVE || weapon === WEAPON_GEYSER) this.hiss('lowpass', 500, 2600, 0.9, 0.55 * g, pan);
    else if (weapon === WEAPON_DOLL) this.tone('triangle', 660, 990, 0.18, 0.25 * g, pan);
    else if (weapon === WEAPON_GRENADE) this.hiss('bandpass', 900, 500, 0.15, 0.3 * g, pan, 0, 3);
    else this.hiss('bandpass', 800, 1600, 0.18, 0.3 * g, pan, 0, 2);
  }

  /** A Roomba swallowing: a breath that rises. */
  inhale(x?: number, y?: number): void {
    const [pan, g] = this.where(x, y);
    this.hiss('bandpass', 500, 1800, 0.65, 0.22 * g, pan, 0, 2);
  }

  splash(x?: number, y?: number): void {
    if (!this.allow('splash')) return;
    const [pan, g] = this.where(x, y);
    this.hiss('lowpass', 3000, 300, 0.7, 0.6 * g, pan);
  }

  freeze(x?: number, y?: number): void {
    if (!this.allow('freeze')) return;
    const [pan, g] = this.where(x, y);
    this.tone('sine', 1800, 2600, 0.5, 0.18 * g, pan);
    this.tone('sine', 2400, 3400, 0.4, 0.12 * g, pan, 0.05);
    this.hiss('highpass', 6000, 3000, 0.5, 0.25 * g, pan);
  }

  zap(x?: number, y?: number): void {
    if (!this.allow('zap')) return;
    const [pan, g] = this.where(x, y);
    this.tone('sawtooth', 220, 80, 0.35, 0.3 * g, pan);
    this.hiss('bandpass', 4000, 2000, 0.3, 0.3 * g, pan, 0, 4);
  }

  chime(open: boolean, x?: number, y?: number): void {
    if (!this.allow('chime')) return;
    const [pan, g] = this.where(x, y);
    this.tone('triangle', open ? 880 : 520, open ? 880 : 520, 0.15, 0.25 * g, pan);
    if (open) this.tone('triangle', 1320, 1320, 0.25, 0.22 * g, pan, 0.09);
  }

  hurt(x?: number, y?: number): void {
    if (!this.allow('hurt')) return;
    const [pan, g] = this.where(x, y);
    this.tone('square', 260, 140, 0.14, 0.12 * g, pan);
  }

  death(x?: number, y?: number): void {
    if (!this.allow('death')) return;
    const [pan, g] = this.where(x, y);
    this.tone('triangle', 440, 70, 0.7, 0.3 * g, pan);
  }

  legacy(cancelled: boolean, x?: number, y?: number): void {
    if (!this.allow('legacy')) return;
    const [pan, g] = this.where(x, y);
    if (cancelled) this.tone('sine', 400, 150, 0.3, 0.2 * g, pan);
    else { this.tone('sine', 660, 660, 0.3, 0.15 * g, pan); this.tone('sine', 990, 990, 0.4, 0.12 * g, pan, 0.1); }
  }

  crunch(x?: number, y?: number): void {
    if (!this.allow('crunch')) return;
    const [pan, g] = this.where(x, y);
    this.hiss('bandpass', 1500, 500, 0.2, 0.5 * g, pan, 0, 2);
    this.hiss('bandpass', 1200, 400, 0.15, 0.4 * g, pan, 0.1, 2);
  }

  grind(x?: number, y?: number): void {
    if (!this.allow('grind')) return;
    const [pan, g] = this.where(x, y);
    this.tone('sawtooth', 70, 50, 0.6, 0.2 * g, pan);
    this.hiss('bandpass', 900, 600, 0.6, 0.35 * g, pan, 0, 1.5);
  }

  whirr(x?: number, y?: number): void {
    if (!this.allow('whirr')) return;
    const [pan, g] = this.where(x, y);
    this.tone('sawtooth', 200, 600, 0.4, 0.12 * g, pan);
  }

  rumble(x?: number, y?: number): void {
    if (!this.allow('rumble')) return;
    const [pan, g] = this.where(x, y);
    this.hiss('lowpass', 300, 60, 2.5, 1.2 * Math.max(0.5, g), pan);
    this.tone('sine', 50, 30, 2, 0.6, pan);
  }

  /** The jetpack's thrust, renewed while it is held. */
  jet(x?: number, y?: number): void {
    if (!this.ctx || this.muted) return;
    const t = this.ctx.currentTime;
    if ((this.last.get('jet') ?? -1) > t - 0.12) return;
    this.last.set('jet', t);
    this.played++;
    const [pan, g] = this.where(x, y);
    this.hiss('bandpass', 900, 600, 0.16, 0.3 * g, pan, 0, 0.8);
  }

  sizzle(x?: number, y?: number): void {
    if (!this.allow('sizzle')) return;
    const [pan, g] = this.where(x, y);
    this.hiss('highpass', 5000, 2500, 1.2, 0.35 * g, pan);
  }

  /** A footstep (soft, every few steps of a walking unit). */
  step(x?: number, y?: number): void {
    if (!this.allow('step')) return;
    const [pan, g] = this.where(x, y);
    this.hiss('bandpass', 700 + Math.random() * 300, 300, 0.05, 0.16 * g, pan, 0, 2);
  }

  jump(x?: number, y?: number): void {
    if (!this.allow('jump')) return;
    const [pan, g] = this.where(x, y);
    this.tone('square', 260, 520, 0.12, 0.07 * g, pan);
  }

  /** A jump pad throws a unit up: a spring. */
  boing(x?: number, y?: number): void {
    if (!this.allow('boing')) return;
    const [pan, g] = this.where(x, y);
    this.tone('triangle', 180, 900, 0.25, 0.14 * g, pan);
    this.tone('sine', 360, 1400, 0.18, 0.06 * g, pan, 0.03);
  }

  /** A unit lands (`hard`: from high, with dust). */
  land(hard: boolean, x?: number, y?: number): void {
    if (!this.allow('land')) return;
    const [pan, g] = this.where(x, y);
    this.hiss('lowpass', hard ? 900 : 600, 90, hard ? 0.22 : 0.1, (hard ? 0.55 : 0.25) * g, pan);
  }

  /** Another unit of the turn highlighted. */
  select(): void {
    if (!this.allow('select')) return;
    this.tone('triangle', 660, 880, 0.07, 0.12);
  }

  /** ACTION: the unit of the turn is locked. */
  lock(): void {
    if (!this.allow('lock')) return;
    this.tone('square', 440, 440, 0.07, 0.08);
    this.tone('square', 660, 660, 0.12, 0.08, 0, 0.07);
  }

  /** A ping (online): a short double blip from the point. */
  ping(x?: number, y?: number): void {
    if (!this.allow('ping')) return;
    const [pan, g] = this.where(x, y);
    this.tone('sine', 1320, 1320, 0.06, 0.14 * g, pan);
    this.tone('sine', 1760, 1760, 0.09, 0.12 * g, pan, 0.08);
  }

  /** An invader put on the map. */
  place(): void {
    if (!this.allow('place')) return;
    this.hiss('lowpass', 800, 120, 0.18, 0.4);
    this.tone('triangle', 330, 220, 0.15, 0.12);
  }

  /** The turn handed over before its time. */
  endTurn(): void {
    if (!this.allow('endTurn')) return;
    this.hiss('bandpass', 1800, 400, 0.3, 0.2, 0, 0, 1.5);
  }

  /** The match is won: a little fanfare (a draw: two low notes). */
  victory(draw: boolean): void {
    if (!this.allow('victory')) return;
    const notes = draw ? [392, 330] : [523, 659, 784, 1047];
    notes.forEach((f, i) => this.tone('square', f, f, i === notes.length - 1 ? 0.6 : 0.16, 0.09, 0, i * 0.15));
    if (!draw) notes.forEach((f, i) => this.tone('triangle', f / 2, f / 2, 0.3, 0.1, 0, i * 0.15));
  }

  /** A new turn. */
  turn(): void {
    if (!this.allow('turn')) return;
    this.tone('triangle', 523, 523, 0.12, 0.2);
    this.tone('triangle', 784, 784, 0.2, 0.18, 0, 0.1);
  }

  /** One of the last seconds of the turn's timer. */
  tick(last: boolean): void {
    if (!this.allow('tick')) return;
    this.tone('square', last ? 1200 : 900, last ? 1200 : 900, 0.05, 0.08);
  }

  click(): void {
    if (!this.allow('click')) return;
    this.tone('triangle', 700, 500, 0.05, 0.12);
  }

  /** The sounds of a tick's events. `unitPos` gives a unit's centre. */
  onEvents(events: readonly GameEvent[], unitPos: (id: number) => [number, number] | null): void {
    if (!this.ctx || this.muted) return;
    for (const e of events) {
      switch (e.type) {
        case 'explosion': if (e.radius > 0) this.explosion(e.radius, e.x, e.y); else this.thud(e.x, e.y); break;
        case 'shot': this.shot(e.x1, e.y1); break;
        case 'launch': { const p = unitPos(e.unit); if (p) this.launch(e.weapon, p[0], p[1]); else this.launch(e.weapon); break; }
        case 'damage': { const p = unitPos(e.unit); if (p) this.hurt(p[0], p[1]); break; }
        case 'death': { const p = unitPos(e.unit); if (p) this.death(p[0], p[1]); break; }
        case 'legacy': this.legacy(e.stage === 0, e.x, e.y); break;
        case 'chest': if (e.what === 'open' || e.what === 'blown') this.chime(true, e.x, e.y); else if (e.what === 'drop') this.chime(false, e.x, e.y); else this.sizzle(e.x, e.y); break;
        case 'drill': this.grind(e.x0, e.y0); break;
        case 'freeze': this.freeze(e.x, e.y); break;
        case 'discharge': this.zap(e.x, e.y); break;
        case 'laser': break; // heard at the launch
        case 'bite': this.crunch(e.x, e.y); break;
        case 'remote': this.whirr(e.tx, e.ty); break;
        case 'meteor': this.explosion(14, e.x, e.y); break;
        case 'surge': this.splash(e.x, e.y); break;
        case 'eruption': this.rumble(e.x, e.y); break;
        case 'inhale': this.inhale(e.x, e.y); break;
        case 'stoneRain': this.rumble(); break; // the whole sky
        case 'cauldron': this.sizzle(e.x, e.y); break;
        case 'pad': this.boing(e.x, e.y); break;
        case 'burst': break; // with the death
      }
    }
  }
}

export const sound = new Sound();
